import React from 'react';
import { Link } from 'react-router-dom';
import { etichettaPagamento, OMAGGIO } from './formePagamento';

/*
 * Cronologia di un ordine e del suo documento (15/10/2026): la stessa nella scheda dell'ordine e in quella
 * del documento (Fatture e ricevute), cosi' da tutte e due si vede tutta la procedura.
 *
 *   1. Ordine creato: data, ora e numero d'ordine.
 *   2. Pagamento registrato: forma di pagamento, data e ora (l'ora c'e' quando c'e' il documento, che nasce
 *      con il pagamento); se non e' pagato, «In attesa di pagamento».
 *   3. Documento emesso: tipo, numero e data; se non c'e', «Documento non emesso».
 *
 * Ogni scheda puo' aggiungere i suoi passi dopo (`extra`: rimborso, invio allo SDI…). Il passo dell'ordine porta
 * all'ordine e quello del documento al documento, quando non si e' gia' li'.
 */

const comeData = (valore) => {
    if (valore === null || valore === undefined || valore === '') return null;
    // Un giorno senza ora (AAAA-MM-GG) e' di quel giorno, non della mezzanotte UTC
    const testo = String(valore);
    const data = /^\d{4}-\d{2}-\d{2}$/.test(testo) ? new Date(`${testo}T00:00:00`) : new Date(valore);
    return isNaN(data.getTime()) ? null : data;
};
const due = (n) => String(n).padStart(2, '0');
const fmtData = (d) => `${due(d.getDate())}/${due(d.getMonth() + 1)}/${d.getFullYear()}`;
const fmtOra = (d) => `${due(d.getHours())}:${due(d.getMinutes())}`;
const stessoGiorno = (a, b) => !!(a && b && fmtData(a) === fmtData(b));

const TIPO_DOCUMENTO = { INVOICE: 'Fattura', RECEIPT: 'Ricevuta' };

/** Gli indirizzi delle schede ordine, per tipo di prodotto. */
export const SCHEDA_ORDINE = {
    subscription: '/struttura/booking/userSubscriptions/',
    course: '/struttura/booking/userCourses/',
    lesson: '/struttura/booking/userLessons/',
    venue: '/struttura/booking/uservenues/',
};

/**
 * I passi della cronologia.
 * - `ordine`: l'ordine come arriva dal server (orderID, createdAt, status, payment_method, paymentDate).
 * - `documento`: { transactionId, type, number, date, createdAt } oppure niente.
 * - `linkOrdine` / `linkDocumento`: dove portano i due passi (si omettono nella scheda dove si e' gia').
 */
export const passiCronologia = ({ ordine = {}, documento, linkOrdine, linkDocumento, extra = [] }) => {
    const passi = [];

    const creato = comeData(ordine.createdAt);
    if (creato || ordine.orderID) {
        passi.push({
            key: 'creato',
            label: 'Ordine creato',
            detail: ordine.orderID ? `Ordine n. ${ordine.orderID}` : '',
            date: creato,
            conOra: true,
            link: linkOrdine,
        });
    }

    const pagato = ordine.status === 'PAID' || ordine.status === 'REFUNDED' || !!ordine.paymentDate;
    if (pagato) {
        const giorno = comeData(ordine.paymentDate);
        // L'ora del pagamento la da' il documento, registrato in quel momento (solo se e' dello stesso giorno)
        const registrato = comeData(documento?.createdAt);
        const conOra = !giorno || stessoGiorno(giorno, registrato);
        passi.push({
            key: 'pagato',
            label: ordine.payment_method === OMAGGIO ? 'Omaggio registrato' : 'Pagamento registrato',
            detail: etichettaPagamento(ordine.payment_method) || '',
            date: conOra ? (registrato || giorno) : giorno,
            conOra: conOra && !!registrato,
        });
    } else if (ordine.status !== 'CANCELLED') {
        passi.push({ key: 'pagato', label: 'Pagamento', detail: 'In attesa di pagamento', tone: 'is-muted' });
    }

    if (documento && documento.number) {
        passi.push({
            key: 'documento',
            label: 'Documento emesso',
            detail: `${TIPO_DOCUMENTO[documento.type] || 'Documento'} n. ${documento.number}`,
            date: comeData(documento.date),
            link: linkDocumento,
        });
    } else {
        passi.push({ key: 'documento', label: 'Documento', detail: 'Documento non emesso', tone: 'is-muted' });
    }

    return [...passi, ...extra.filter(Boolean)];
};

export const CronologiaOrdine = ({ passi }) => {
    if (!passi || passi.length === 0) return <div className="sid-order-muted">Nessun evento registrato</div>;
    return (
        <ol className="sid-timeline">
            {passi.map((passo) => {
                const data = comeData(passo.date);
                return (
                    <li key={passo.key} className={`sid-timeline__item ${passo.tone || ''}`}>
                        <div className="sid-timeline__label">
                            {passo.link ? <Link to={passo.link}>{passo.label}</Link> : passo.label}
                        </div>
                        {passo.detail && <div className="sid-timeline__detail">{passo.detail}</div>}
                        {passo.errors && passo.errors.length > 0 && (
                            <ul className="sid-timeline__errors">
                                {passo.errors.map((err, index) => (
                                    <li key={index}><strong>{err.Codice}:</strong> {err.Descrizione}</li>
                                ))}
                            </ul>
                        )}
                        {data && (
                            <div className="sid-timeline__date">{fmtData(data)}{passo.conOra ? ` alle ${fmtOra(data)}` : ''}</div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
};

export default CronologiaOrdine;
